/**
 * Telemetría del producto — cliente frontend (Sprint 12 · 1).
 *
 * Encapsula el envío de eventos a `POST /events/track`. Diseñado para
 * que ninguna falla de analytics rompa el flujo del usuario:
 *
 * - El POST es fire-and-forget. No bloquea la UI.
 * - Si la red falla, encolamos el evento en `localStorage` y reintentamos
 *   en el próximo `track()` o `flushQueue()`.
 * - Si el browser se cierra a media flush, los eventos quedan en
 *   localStorage para el próximo arranque.
 *
 * El módulo NO depende de React: puede usarse desde funciones puras,
 * effects, handlers. El hook `useTrackEvent` (en `lib/analytics.tsx`)
 * es solo azúcar para componentes.
 */

import { supabase } from "./supabase";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";
const TRACK_ENDPOINT = `${API_BASE}/v1/events/track`;
const QUEUE_STORAGE_KEY = "kaptar.analytics.queue.v1";
const SESSION_STORAGE_KEY = "kaptar.analytics.session.v1";

// Cap defensivo. Si por alguna razón se acumulan 1000+ eventos en
// localStorage (red caída por días) cortamos los más viejos.
const QUEUE_MAX_SIZE = 500;

// Validación local — debe coincidir con la regex del backend
// (`subsystems/analytics/events.py::_EVENT_NAME_PATTERN`).
const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

export interface TrackPayload {
  event_name: string;
  properties?: Record<string, unknown>;
  session_id?: string;
}

interface QueuedEvent extends TrackPayload {
  queuedAt: number;
}

// ----- Session id ---------------------------------------------------

/**
 * Devuelve un session id estable para esta pestaña. Se persiste en
 * sessionStorage (sobrevive a refreshes pero NO a cerrar la pestaña).
 * Útil para correlacionar eventos pre-auth → post-login.
 */
export function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage no disponible (modo incógnito con restricciones).
    // Devolvemos un id por llamada — la correlación se pierde pero el
    // tracking sigue.
    return "";
  }
}

// ----- Queue --------------------------------------------------------

function readQueue(): QueuedEvent[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is QueuedEvent =>
        typeof e === "object" && e !== null && typeof (e as QueuedEvent).event_name === "string",
    );
  } catch {
    return [];
  }
}

function writeQueue(events: QueuedEvent[]): void {
  if (typeof window === "undefined") return;
  try {
    // Mantener solo los QUEUE_MAX_SIZE más recientes.
    const trimmed =
      events.length > QUEUE_MAX_SIZE ? events.slice(events.length - QUEUE_MAX_SIZE) : events;
    window.localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // Quota exceeded o storage deshabilitado. Sin recovery: descartar.
  }
}

function enqueue(payload: TrackPayload): void {
  const queue = readQueue();
  queue.push({ ...payload, queuedAt: Date.now() });
  writeQueue(queue);
}

// ----- Send ---------------------------------------------------------

async function getAccessToken(): Promise<string | null> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * POST único al endpoint. Devuelve `true` si el server aceptó (2xx),
 * `false` si hubo error de red o 5xx. Los 4xx (event_name mal formado)
 * también devuelven `false` pero el caller no debe reintentar — el
 * problema es la data, no el transporte.
 */
async function postEvent(payload: TrackPayload): Promise<{ ok: boolean; retriable: boolean }> {
  try {
    const token = await getAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const body = JSON.stringify({
      event_name: payload.event_name,
      properties: payload.properties ?? null,
      session_id: payload.session_id ?? null,
    });

    const res = await fetch(TRACK_ENDPOINT, {
      method: "POST",
      headers,
      body,
      // No bloquear navigations con este request.
      keepalive: true,
    });

    if (res.ok) return { ok: true, retriable: false };
    // 4xx no es retriable — la data está mal y reintentarla solo
    // ensucia los logs. 5xx sí (problema temporal del server).
    return { ok: false, retriable: res.status >= 500 };
  } catch {
    // Network error / DNS / CORS preflight: retriable.
    return { ok: false, retriable: true };
  }
}

// ----- Flush --------------------------------------------------------

let flushInFlight = false;

/**
 * Reintenta los eventos pendientes. Llamada desde:
 * - Cada `track()` (oportunista — si la red ya está OK, drenamos).
 * - El consumidor explícito si quiere forzar (ej. antes de logout).
 *
 * Idempotente y reentrante-safe: si ya hay un flush en curso, los
 * siguientes calls retornan sin hacer nada hasta que termine.
 */
export async function flushQueue(): Promise<void> {
  if (flushInFlight) return;
  flushInFlight = true;
  try {
    const queue = readQueue();
    if (queue.length === 0) return;

    const survivors: QueuedEvent[] = [];
    for (const event of queue) {
      const result = await postEvent(event);
      if (!result.ok && result.retriable) {
        // Lo dejamos en la cola.
        survivors.push(event);
      }
      // ok=true → drop. ok=false && !retriable → drop (data mal formada).
    }
    writeQueue(survivors);
  } finally {
    flushInFlight = false;
  }
}

// ----- Track --------------------------------------------------------

/**
 * Reporta un evento al backend. Fire-and-forget.
 *
 * Si la red falla, el evento queda en la cola de `localStorage` para
 * el próximo `track()` o `flushQueue()`. Si el `event_name` no cumple
 * el formato snake_case del backend, descartamos en cliente para no
 * generar 422s en cascada.
 *
 * Esta función NUNCA rechaza. Es seguro llamarla desde useEffect
 * sin try/catch.
 */
export function track(payload: TrackPayload): void {
  if (typeof window === "undefined") return;
  if (!EVENT_NAME_PATTERN.test(payload.event_name)) {
    // En dev, hacer visible el error. En prod no rompemos nada.
    if (import.meta.env.DEV) {
      console.warn(
        `[analytics] event_name inválido (esperado snake_case): "${payload.event_name}"`,
      );
    }
    return;
  }

  // Adjuntar session_id si no vino. `getOrCreateSessionId` puede
  // devolver "" si sessionStorage está bloqueado — en ese caso lo
  // dejamos undefined para que el body no incluya un session_id vacío.
  const session = payload.session_id ?? getOrCreateSessionId();
  const withSession: TrackPayload = {
    ...payload,
    session_id: session || undefined,
  };

  // Fire-and-forget. Si falla, encolamos.
  void postEvent(withSession).then((result) => {
    if (!result.ok && result.retriable) {
      enqueue(withSession);
    }
  });

  // Oportunista: intentamos drenar cola residual del pasado.
  // No esperamos al resultado.
  void flushQueue();
}

// ----- Helpers para tests -------------------------------------------

/** Solo para tests — limpia la cola persistida. */
export function _clearQueueForTests(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(QUEUE_STORAGE_KEY);
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
  flushInFlight = false;
}

/** Solo para tests — lee la cola sin side effects. */
export function _readQueueForTests(): QueuedEvent[] {
  return readQueue();
}
